import { MakeLogicType, actions, afterMount, connect, kea, listeners, path, reducers, selectors } from 'kea'
import { loaders } from 'kea-loaders'
import { router } from 'kea-router'

import api from 'lib/api'
import { PaginationManual } from 'lib/lemon-ui/PaginationControl'
import { removeProjectIdIfPresent } from 'lib/utils/kea-router'
import { objectClean } from 'lib/utils/objects'
import { toParams } from 'lib/utils/url'
import { sessionRecordingEventUsageLogic } from 'scenes/session-recordings/sessionRecordingEventUsageLogic'
import { urls } from 'scenes/urls'

import {
    ReplayTabs,
    SavedSessionRecordingPlaylistsFilters,
    SavedSessionRecordingPlaylistsResult,
    SessionRecordingPlaylistType,
} from '~/types'

import { deletePlaylist, stripSessionIds } from '../playlist/playlistUtils'
import { asUniversalFilters } from '../playlist/sessionRecordingsPlaylistLogic'

export const PLAYLISTS_PER_PAGE = 30

export const DEFAULT_PLAYLIST_FILTERS = {
    createdBy: 'All users',
    page: 1,
    dateFrom: 'all',
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingSavedFiltersLogicValues {
    appliedSavedFilter: SessionRecordingPlaylistType | null
    filters: Record<string, any> | SavedSessionRecordingPlaylistsFilters
    loadSavedFiltersFailed: boolean
    paginationSavedFilters: PaginationManual
    pendingFilterApplication: SessionRecordingPlaylistType | null
    playlists: SavedSessionRecordingPlaylistsResult
    playlistsLoading: boolean
    savedFilters: SavedSessionRecordingPlaylistsResult
    savedFiltersLoading: boolean
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingSavedFiltersLogicActions {
    reportRecordingPlaylistCreated: (source: 'duplicate' | 'filters' | 'new' | 'pin') => {
        source: 'duplicate' | 'filters' | 'new' | 'pin'
    } // sessionRecordingEventUsageLogic
    checkForSavedFilterRedirect: () => {
        value: true
    }
    clearPendingFilterApplication: () => {
        value: true
    }
    deletePlaylist: (playlist: SessionRecordingPlaylistType) => {
        playlist: SessionRecordingPlaylistType
    }
    deletePlaylistFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    deletePlaylistSuccess: (
        playlists: SavedSessionRecordingPlaylistsResult,
        payload?: {
            playlist: SessionRecordingPlaylistType
        }
    ) => {
        playlists: SavedSessionRecordingPlaylistsResult
        payload?: {
            playlist: SessionRecordingPlaylistType
        }
    }
    loadSavedFilters: () => {
        value: true
    }
    loadSavedFiltersFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    loadSavedFiltersSuccess: (
        savedFilters: SavedSessionRecordingPlaylistsResult,
        payload?: {
            value: true
        }
    ) => {
        savedFilters: SavedSessionRecordingPlaylistsResult
        payload?: {
            value: true
        }
    }
    requestApplySavedFilter: (filter: SessionRecordingPlaylistType) => {
        filter: SessionRecordingPlaylistType
    }
    setAppliedSavedFilter: (appliedSavedFilter: SessionRecordingPlaylistType | null) => {
        appliedSavedFilter: SessionRecordingPlaylistType | null
    }
    setSavedPlaylistsFilters: (filters: Partial<SavedSessionRecordingPlaylistsFilters>) => {
        filters: Partial<SavedSessionRecordingPlaylistsFilters>
    }
    updatePlaylist: (
        shortId: SessionRecordingPlaylistType['short_id'],
        properties: Partial<SessionRecordingPlaylistType>
    ) => {
        properties: Partial<SessionRecordingPlaylistType>
        shortId: string
    }
    updatePlaylistFailure: (
        error: string,
        errorObject?: any
    ) => {
        error: string
        errorObject?: any
    }
    updatePlaylistSuccess: (
        playlists: {
            count: number
            filters?: SavedSessionRecordingPlaylistsFilters | null | undefined
            next?: string | null | undefined
            previous?: string | null | undefined
            results: SessionRecordingPlaylistType[]
        },
        payload?: {
            properties: Partial<SessionRecordingPlaylistType>
            shortId: string
        }
    ) => {
        playlists: {
            count: number
            filters?: SavedSessionRecordingPlaylistsFilters | null | undefined
            next?: string | null | undefined
            previous?: string | null | undefined
            results: SessionRecordingPlaylistType[]
        }
        payload?: {
            properties: Partial<SessionRecordingPlaylistType>
            shortId: string
        }
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface sessionRecordingSavedFiltersLogicMeta {
    __keaTypeGenInternalSelectorTypes: {
        paginationSavedFilters: (
            filters: Record<string, any> | SavedSessionRecordingPlaylistsFilters,
            savedFilters: SavedSessionRecordingPlaylistsResult
        ) => PaginationManual
    }
}

export type sessionRecordingSavedFiltersLogicType = MakeLogicType<
    sessionRecordingSavedFiltersLogicValues,
    sessionRecordingSavedFiltersLogicActions,
    Record<string, any>,
    sessionRecordingSavedFiltersLogicMeta
>

export const sessionRecordingSavedFiltersLogic = kea<sessionRecordingSavedFiltersLogicType>([
    path(() => ['scenes', 'session-recordings', 'filters', 'sessionRecordingSavedFiltersLogic']),
    connect(() => ({
        actions: [sessionRecordingEventUsageLogic, ['reportRecordingPlaylistCreated']],
    })),
    actions(() => ({
        setSavedPlaylistsFilters: (filters: Partial<SavedSessionRecordingPlaylistsFilters>) => ({
            filters,
        }),
        loadSavedFilters: true,
        updatePlaylist: (
            shortId: SessionRecordingPlaylistType['short_id'],
            properties: Partial<SessionRecordingPlaylistType>
        ) => ({ shortId, properties }),
        deletePlaylist: (playlist: SessionRecordingPlaylistType) => ({ playlist }),
        checkForSavedFilterRedirect: true,
        setAppliedSavedFilter: (appliedSavedFilter: SessionRecordingPlaylistType | null) => ({ appliedSavedFilter }),
        requestApplySavedFilter: (filter: SessionRecordingPlaylistType) => ({ filter }),
        clearPendingFilterApplication: true,
    })),
    reducers(() => ({
        filters: [
            DEFAULT_PLAYLIST_FILTERS as SavedSessionRecordingPlaylistsFilters | Record<string, any>,
            {
                setSavedPlaylistsFilters: (state, { filters }) =>
                    objectClean({
                        ...Object.fromEntries(Object.entries(state || {}).filter(([key]) => key in filters)),
                        ...filters,
                        ...('page' in filters ? {} : { page: 1 }),
                    }),
            },
        ],
        appliedSavedFilter: [
            null as SessionRecordingPlaylistType | null,
            {
                setAppliedSavedFilter: (_, { appliedSavedFilter }) => appliedSavedFilter,
            },
        ],
        pendingFilterApplication: [
            null as SessionRecordingPlaylistType | null,
            {
                requestApplySavedFilter: (_, { filter }) => filter,
                clearPendingFilterApplication: () => null,
            },
        ],
        loadSavedFiltersFailed: [
            false,
            {
                loadSavedFilters: () => false,
                loadSavedFiltersSuccess: () => false,
                loadSavedFiltersFailure: () => true,
            },
        ],
    })),
    loaders(({ values, actions }) => ({
        savedFilters: {
            __default: { results: [], count: 0, filters: null } as SavedSessionRecordingPlaylistsResult,
            loadSavedFilters: async (_, breakpoint) => {
                const filters = { ...values.filters }

                const params = {
                    limit: PLAYLISTS_PER_PAGE,
                    offset: Math.max(0, (filters.page - 1) * PLAYLISTS_PER_PAGE),
                    order: '-last_modified_at',
                    created_by: filters.createdBy && filters.createdBy !== 'All users' ? filters.createdBy : undefined,
                    search: filters.search || undefined,
                    date_from: undefined,
                    date_to: undefined,
                    pinned: undefined,
                    type: 'filters',
                }

                const response = await api.recordings.listPlaylists(toParams(params))
                breakpoint()

                return response
            },
        },
        playlists: {
            __default: { results: [], count: 0, filters: null } as SavedSessionRecordingPlaylistsResult,
            updatePlaylist: async ({ shortId, properties }, breakpoint) => {
                await breakpoint(100)
                const updatedPlaylist = await api.recordings.updatePlaylist(shortId, properties)
                breakpoint()

                const index = values.playlists.results.findIndex((x) => x.short_id === updatedPlaylist.short_id)
                if (index > -1) {
                    values.playlists.results[index] = updatedPlaylist
                }

                return { ...values.playlists, results: [...values.playlists.results] }
            },
            deletePlaylist: async ({ playlist }) => {
                await deletePlaylist(playlist, () => actions.loadSavedFilters())
                values.playlists.results = values.playlists.results.filter((x) => x.short_id !== playlist.short_id)
                actions.loadSavedFilters()
                return values.playlists
            },
        },
    })),
    listeners(({ actions }) => ({
        setSavedPlaylistsFilters: () => {
            actions.loadSavedFilters()
        },
        checkForSavedFilterRedirect: async () => {
            //If you want to load a saved filter via GET param, you can do it like this: ?savedFilterId=bndnfkxL
            const { savedFilterId } = router.values.searchParams
            if (savedFilterId) {
                // Capture the route before the fetch. The load can resolve after the user has already
                // navigated elsewhere (e.g. to Replay vision) - only redirect if they are still here.
                const pathnameBeforeLoad = removeProjectIdIfPresent(router.values.location.pathname)
                const savedFilter = await api.recordings.getPlaylist(savedFilterId)
                if (savedFilter && removeProjectIdIfPresent(router.values.location.pathname) === pathnameBeforeLoad) {
                    router.actions.push(
                        urls.replay(ReplayTabs.Home, stripSessionIds(asUniversalFilters(savedFilter.filters)))
                    )
                    actions.setAppliedSavedFilter(savedFilter)
                }
            }
        },
    })),

    selectors(({ actions }) => ({
        paginationSavedFilters: [
            (s) => [s.filters, s.savedFilters],
            (
                filters: Record<string, any> | SavedSessionRecordingPlaylistsFilters,
                savedFilters: SavedSessionRecordingPlaylistsResult
            ): PaginationManual => {
                return {
                    controlled: true,
                    pageSize: PLAYLISTS_PER_PAGE,
                    currentPage: filters.page,
                    entryCount: savedFilters.count,
                    onBackward: savedFilters.previous
                        ? () => {
                              actions.setSavedPlaylistsFilters({
                                  page: filters.page - 1,
                              })
                              actions.loadSavedFilters()
                          }
                        : undefined,
                    onForward: savedFilters.next
                        ? () => {
                              actions.setSavedPlaylistsFilters({
                                  page: filters.page + 1,
                              })
                              actions.loadSavedFilters()
                          }
                        : undefined,
                }
            },
        ],
    })),
    afterMount(({ actions }) => {
        actions.loadSavedFilters()
        actions.checkForSavedFilterRedirect()
    }),
])
